import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";

const ROLES = ["owner", "worker", "stable", "service_provider", "seller", "buyer"];

export default function Register() {
  const { t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer", location: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error(t("terms_must_accept"));
      return;
    }
    setBusy(true);
    try {
      await register({ ...form, terms_accepted: true });
      toast.success("Account created");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="register-page" className="pt-28 pb-20 max-w-2xl mx-auto px-6">
      <div className="overline text-[#143122]">{t("signup")}</div>
      <h1 className="mt-2 font-serif-display text-4xl text-[#143122]">{t("create_account")}</h1>

      <form onSubmit={submit} className="mt-10 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1.5">{t("role")}</label>
          <p className="text-xs text-[#1A1F1C]/60 mb-3 leading-relaxed">
            {t("role_hint")}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {ROLES.map((r) => (
              <div
                key={r}
                data-testid={`role-${r}`}
                data-selected={form.role === r}
                onClick={() => setForm({ ...form, role: r })}
                className="kav-role-card"
              >
                <div className="overline">{r.replace("_", " ")}</div>
                <div className="mt-1 font-serif-display text-lg leading-tight">
                  {t(`role_${r}`)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("name")}</label>
            <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="kav-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("location")}</label>
            <input data-testid="register-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="kav-input" placeholder="Helsinki, FI" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("email")}</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="kav-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("password")}</label>
            <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="kav-input" />
          </div>
        </div>

        {/* Terms & Conditions */}
        <div
          data-testid="terms-section"
          className="rounded-xl border border-[#143122]/15 bg-[#F5F1E8]/60 p-4 md:p-5"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="overline text-[#143122]">{t("terms_title")}</div>
              <p className="mt-1 text-sm text-[#1A1F1C]/80">{t("terms_intro")}</p>
            </div>
            <button
              type="button"
              data-testid="terms-toggle"
              onClick={() => setShowTerms((v) => !v)}
              className="shrink-0 inline-flex items-center gap-1 text-sm text-[#143122] hover:underline"
            >
              {showTerms ? t("terms_hide") : t("terms_read_more")}
              {showTerms ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>

          {showTerms && (
            <div
              data-testid="terms-content"
              className="mt-4 space-y-3 text-sm leading-relaxed text-[#1A1F1C]/85 border-t border-[#143122]/10 pt-4"
            >
              <p>{t("terms_p1")}</p>
              <p>{t("terms_p2")}</p>
              <p>{t("terms_p3")}</p>
              <p>{t("terms_p4")}</p>
            </div>
          )}

          <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
            <input
              data-testid="terms-accept-checkbox"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[#143122]/40 text-[#143122] accent-[#143122]"
            />
            <span className="text-sm text-[#1A1F1C]">
              {t("terms_accept_label")}
            </span>
          </label>
        </div>

        <button
          data-testid="register-submit"
          disabled={busy || !termsAccepted}
          type="submit"
          className="kav-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "..." : t("create_account")}
        </button>
      </form>
      <p className="mt-6 text-sm text-[#1A1F1C]/70">
        {t("have_account")} <Link to="/login" className="text-[#143122] underline">{t("login")}</Link>
      </p>
    </div>
  );
}
